

export const dynamic = 'force-dynamic';

import { useState, useEffect } from "react";
import VendorReportsLayout from "../VendorReportsLayout";
import { reportsApi } from "@/lib/api";
import { formatDate } from "@/lib/dateFormat";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Assignment {
  id: number;
  vendor_name: string;
  vendor_type?: string;
  event_name: string;
  client_name?: string;
  event_date: string;
  description?: string;
  quantity: number;
  unit?: string;
  status: string;
  commission_amount: number;
  total_paid: number;
  created_at: string;
  unit_price?: number;
  cost_price?: number;
  revenue?: number;
  cost?: number;
  outstanding_balance?: number;
}

interface AssignmentsResponse {
  current_page: number;
  data: Assignment[];
  last_page: number;
  per_page: number;
  total: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n ?? 0);

const statusColors: Record<string, string> = {
  confirmed: "bg-emerald-100 text-emerald-700",
  draft: "bg-stone-100 text-stone-600",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-violet-100 text-violet-700",
  cancelled: "bg-rose-100 text-rose-700",
  pending: "bg-amber-100 text-amber-700",
  paid: "bg-teal-100 text-teal-700",
};

// ─── Page Component ───────────────────────────────────────────────────────────

export default function VendorAssignmentsPage() {
  const [period, setPeriod] = useState<{ from: string; to: string }>({ from: "", to: "" });
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    setLoading(true);
    const params: any = { page: String(page), per_page: "20" };
    if (period.from && period.to) {
      params.from_date = period.from;
      params.to_date = period.to;
    }
    if (statusFilter) params.status = statusFilter;

    reportsApi
      .vendorAssignments(params)
      .then((res: any) => {
        if (res?.success) {
          const data = res.data.assignments;
          setAssignments(data.data || data || []);
          setLastPage(data.last_page ?? 1);
        }
      })
      .catch((err) => console.error("Failed to load vendor assignments:", err))
      .finally(() => setLoading(false));
  }, [period, page, statusFilter]);

  return (
    <VendorReportsLayout onPeriodChange={setPeriod}>
      {/* ── Filters ── */}
      <div className="flex items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-1.5 text-xs rounded-lg border border-stone-200 focus:border-amber-400 outline-none bg-white"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="paid">Paid</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* ── Assignments Table ── */}
      <div className="bg-white rounded-2xl border border-stone-200">
        <div className="px-5 py-4 border-b border-stone-100">
          <h3 className="font-bold text-stone-800 font-serif text-sm">Vendor Assignment History</h3>
        </div>

        {loading ? (
          <div className="p-5 space-y-3 animate-pulse">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-stone-50 rounded-xl" />
            ))}
          </div>
        ) : assignments.length === 0 ? (
          <div className="p-8 text-center text-sm text-stone-400 italic">No assignments found for this period.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase">Vendor</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Event</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Date</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Status</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Qty</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Commission</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Paid</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-50">
                  {assignments.map((assignment) => (
                    <tr key={assignment.id} className="hover:bg-stone-50/50 transition-colors">
                      <td className="px-5 py-3">
                        <div className="font-semibold text-stone-800">{assignment.vendor_name}</div>
                        {assignment.vendor_type && <div className="text-xs text-stone-400">{assignment.vendor_type}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-stone-700">{assignment.event_name}</div>
                        {assignment.client_name && <div className="text-xs text-stone-400">{assignment.client_name}</div>}
                      </td>
                      <td className="px-4 py-3 text-stone-500 text-xs">{formatDate(assignment.event_date)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[assignment.status] ?? "bg-stone-100 text-stone-600"}`}>
                          {assignment.status?.replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-stone-600">
                        {assignment.quantity} {assignment.unit || ""}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-violet-700">{fmt(assignment.commission_amount)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-emerald-700">{fmt(assignment.total_paid)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {lastPage > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-stone-100">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-xs rounded-lg border border-stone-200 disabled:opacity-40 hover:border-stone-300 transition-colors"
                >
                  Previous
                </button>
                <span className="text-xs text-stone-500">Page {page} of {lastPage}</span>
                <button
                  onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                  disabled={page === lastPage}
                  className="px-3 py-1.5 text-xs rounded-lg border border-stone-200 disabled:opacity-40 hover:border-stone-300 transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </VendorReportsLayout>
  );
}
